"use client";

import React, { useEffect, useState } from "react";
import { Layers, Tag, CircleAlert } from "lucide-react";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { cn } from "@/lib/utils";

declare global {
  interface Window {
    wvcClient: any;
  }
}

function SingleProductSkeleton() {
  return (
    <div data-section-id="34105" className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 animate-pulse">
      <div className="w-full">
        <div className="bg-muted/20 aspect-[3/4] w-full" />
      </div>
      <div className="flex flex-col gap-6">
        <div className="space-y-3">
          <div className="h-10 bg-muted/20 w-3/4" />
          <div className="h-8 bg-muted/20 w-1/3" />
        </div>
        <div className="space-y-2">
          <div className="h-4 bg-muted/20 w-full" />
          <div className="h-4 bg-muted/20 w-full" />
          <div className="h-4 bg-muted/20 w-2/3" />
        </div>
        <div className="h-px bg-muted/20 w-full my-2" />
        <div className="h-12 bg-muted/20 w-1/2" />
        <div className="h-px bg-muted/20 w-full my-2" />
        <div className="flex gap-2">
          <div className="h-6 bg-muted/20 w-16" />
          <div className="h-6 bg-muted/20 w-16" />
        </div>
      </div>
    </div>
  );
}

function SingleProductConsumer() {
  const { products, loading, error } = useEcommerceProducts();

  if (loading) {
    return <SingleProductSkeleton />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center text-destructive">
        <CircleAlert className="w-10 h-10 mb-4" />
        <h3 className="text-lg font-bold font-default tracking-tight">Ritual Interrupted</h3>
        <p className="text-sm font-default text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <h3 className="text-lg font-bold font-default tracking-tight">Gathering Not Found</h3>
        <p className="text-muted-foreground font-default">This ritual has faded into the ether or is yet to be manifested.</p>
        <Link to="#" className="mt-4 text-primary font-default hover:text-primary/80 transition-colors underline decoration-primary/30 underline-offset-4">
          Return to Sanctuary
        </Link>
      </div>
    );
  }

  const product = products[0];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16">
      {}
      <div className="w-full">
        <div className="overflow-hidden border border-border/50 bg-muted/10 shadow-2xl">
          <AspectRatio ratio={3 / 4}>
            {product.featured_image_url ? (
              <img
                src={product.featured_image_url}
                alt={product.title}
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-110 grayscale-[0.2] hover:grayscale-0"
                loading="eager"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-muted/20">
                <span className="text-xs font-default uppercase tracking-widest">Image Veiled</span>
              </div>
            )}
          </AspectRatio>
        </div>
      </div>

      {}
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default text-foreground tracking-tighter mb-3 leading-none">
            {product.title}
          </h1>
          
          <div className="flex items-baseline gap-3 mb-2">
            {product.discounted_price ? (
              <>
                <span className="text-3xl font-bold font-default text-primary tracking-tight">
                  ${product.discounted_price}
                </span>
                <span className="text-xl font-default text-muted-foreground line-through decoration-secondary/50">
                  ${product.price}
                </span>
              </>
            ) : (
              <span className="text-3xl font-bold font-default text-primary tracking-tight">
                ${product.price}
              </span>
            )}
          </div>
        </div>

        {product.short_description && (
          <div
            className="prose prose-neutral dark:prose-invert max-w-none text-muted-foreground leading-relaxed font-default text-lg"
            dangerouslySetInnerHTML={{ __html: product.short_description }}
          />
        )}

        <Separator className="my-2 bg-border/30" />

        <div className="py-2">
          <AddToCartButton
            productId={product.id}
            variant="default"
            size="lg"
            className="w-full"
            buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none w-full md:w-auto min-w-[240px] h-14 text-base font-bold font-default uppercase tracking-widest shadow-xl transition-all hover:shadow-primary/20"
            showQuantity={true}
            text="Secure Your Space"
          />
        </div>

        <Separator className="my-2 bg-border/30" />

        <div className="space-y-4">
          {product.categories && product.categories.length > 0 && (
            <div className="flex items-start gap-3">
              <Layers className="w-5 h-5 mt-0.5 text-primary/60 shrink-0" />
              <div className="flex flex-wrap gap-2">
                {product.categories.map((cat, i) => (
                  <Link key={cat.id} to={cat.link} data-index={i}>
                    <Badge variant="secondary" className="bg-muted/30 hover:bg-primary/20 text-foreground border border-[#a855f7] rounded-md px-4 py-1 text-xs font-default uppercase tracking-wider transition-colors">
                      {cat.name}
                    </Badge>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {product.tags && product.tags.length > 0 && (
            <div className="flex items-start gap-3">
              <Tag className="w-5 h-5 mt-0.5 text-primary/60 shrink-0" />
              <div className="flex flex-wrap gap-2">
                {product.tags.map((tag, i) => (
                  <Link key={tag.id} to={tag.link} data-index={i}>
                    <span className="text-xs font-default uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors">
                      #{tag.name}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {}
      <div className="col-span-1 md:col-span-2 mt-8 md:mt-16">
        <div className="border-t border-border/30 pt-12">
          <h2 className="text-3xl font-bold font-default mb-8 text-foreground tracking-tighter uppercase">The Ritual Experience</h2>
          <div
            className="prose prose-lg prose-neutral dark:prose-invert max-w-none text-muted-foreground font-default leading-loose"
            dangerouslySetInnerHTML={{ 
              __html: product.long_description || product.short_description || "<p>The mysteries of this experience are yet to be revealed.</p>" 
            }}
          />
        </div>
        
        {}
        {product.customFields && Object.keys(product.customFields).length > 0 && (
           <div className="mt-12 border-t border-border/30 pt-12">
              <h3 className="text-2xl font-bold font-default mb-8 text-foreground tracking-tight uppercase">Technical Resonance</h3>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-6">
                 {Object.entries(product.customFields).map(([key, value], i) => (
                    typeof value === 'string' || typeof value === 'number' ? (
                      <div key={key} data-index={i} className="flex flex-col pb-6 border-b border-border/20 last:border-0">
                        <dt className="text-xs font-bold font-default text-primary uppercase tracking-widest mb-2">
                          {key.replace(/_/g, ' ')}
                        </dt>
                        <dd className="text-lg text-foreground font-default tracking-tight">
                          {value}
                        </dd>
                      </div>
                    ) : null
                 ))}
              </dl>
           </div>
        )}
      </div>
    </div>
  );
}

export default function SingleProductTemplateSection() {
  const [wpQuery, setWpQuery] = useState<any>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    if (typeof window !== "undefined" && window.wvcClient) {
      try {
        const query = window.wvcClient.getCurrentWPQuery();
        setWpQuery(query);
      } catch (e) {
        console.error("Failed to get WP Query:", e);
      }
    }
  }, []);

  if (!isMounted) return null;

  return (
    <section id="single-product-template-section" className="dark bg-background text-foreground py-16 md:py-24 overflow-hidden relative">
      {}
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-primary/5 blur-[120px] rounded-lg -translate-y-1/2 translate-x-1/4 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-secondary/5 blur-[100px] rounded-lg translate-y-1/4 -translate-x-1/4 pointer-events-none" />
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {wpQuery ? (
          <EcommerceProductsProvider wp_query={wpQuery} postType="product">
            <SingleProductConsumer />
          </EcommerceProductsProvider>
        ) : (
          <SingleProductSkeleton />
        )}
      </div>
    </section>
  );
}